import type {
  PoleResult,
  TechCalcDrawing,
  TechCalcLineFeature,
  TechCalcPointFeature,
} from "../types";
import {
  calcSteps,
  drawingLines,
  drawingPoints,
  fmtBundles,
  fmtForce,
  fmtMeters,
  poleDisplayLabel,
  roman,
} from "../utils";

/**
 * Display name of a node, same priority as on the map (historyczny → pełny → ref);
 * access nodes read "przyłącze" (owner: no A1/A2 on the card), optionally
 * disambiguated with the fetched address (table only).
 */
function nodeDisplayName(
  node: TechCalcPointFeature | undefined,
  fallbackRef: string,
  withAddress = false,
): string {
  if (!node) return fallbackRef;
  if (node.properties.kind === "access") {
    if (withAddress) {
      const a = node.properties.address;
      const addr = a ? [a.street, a.house_number].filter(Boolean).join(" ") : "";
      return addr ? `przyłącze (${addr})` : "przyłącze";
    }
    return "przyłącze";
  }
  return poleDisplayLabel(node.properties);
}

interface SpanArm {
  numeral: string; // P.I, P.II…
  span: TechCalcLineFeature;
  otherRef: string;
  /** Display name of the far node (map priority; access = "przyłącze"). */
  otherName: string;
  /** Same, with the fetched address for table disambiguation. */
  otherNameWithAddress: string;
  /** Unit direction in screen space (x → east, y → down = south). */
  ux: number;
  uy: number;
  lengthM: number;
  manual: boolean;
}

/** Star-of-spans geometry for the schematic: directions from real WGS84 coordinates. */
function buildArms(
  pole: TechCalcPointFeature,
  spans: TechCalcLineFeature[],
  nodesByRef: Map<string, TechCalcPointFeature>,
): SpanArm[] {
  const [lngA, latA] = pole.geometry.coordinates;
  const cosLat = Math.cos((latA * Math.PI) / 180);
  return spans.map((span, i) => {
    const otherRef =
      span.properties.from_ref === pole.properties.ref
        ? span.properties.to_ref
        : span.properties.from_ref;
    const other = nodesByRef.get(otherRef);
    // Direction from the span's own geometry end nearest the OTHER node (fallback:
    // straight to the other node's point).
    const [lngB, latB] =
      other?.geometry.coordinates ??
      span.geometry.coordinates[
        span.properties.from_ref === pole.properties.ref
          ? span.geometry.coordinates.length - 1
          : 0
      ];
    const dx = (lngB - lngA) * cosLat;
    const dy = latB - latA;
    const len = Math.hypot(dx, dy) || 1;
    return {
      numeral: `P.${roman(i + 1)}`,
      span,
      otherRef,
      otherName: nodeDisplayName(other, otherRef),
      otherNameWithAddress: nodeDisplayName(other, otherRef, true),
      ux: dx / len,
      uy: -dy / len, // screen y grows downwards; north up
      lengthM: span.properties.length_override ?? span.properties.length_m,
      manual: span.properties.length_override != null,
    };
  });
}

function StarSchematic({
  pole,
  arms,
}: {
  pole: TechCalcPointFeature;
  arms: SpanArm[];
}) {
  const W = 460;
  const H = 380;
  const cx = W / 2;
  const cy = H / 2 + 10;
  const R = 140;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="mx-auto block w-full max-w-[560px]"
      role="img"
      aria-label={`Schemat przęseł słupa ${pole.properties.ref}`}
    >
      {/* north arrow — small, tucked into the schematic's corner */}
      <g stroke="#000" strokeWidth="1.1" fill="none">
        <line x1={W - 20} y1={34} x2={W - 20} y2={16} />
        <polyline
          points={`${W - 24},22 ${W - 20},14 ${W - 16},22`}
          fill="#000"
          stroke="none"
        />
      </g>
      <text
        x={W - 20}
        y={45}
        textAnchor="middle"
        fontSize="9"
        fontFamily="monospace"
      >
        N
      </text>

      {arms.map((a) => {
        const x2 = cx + a.ux * R;
        const y2 = cy + a.uy * R;
        const mx = cx + a.ux * R * 0.55;
        const my = cy + a.uy * R * 0.55;
        // Kąt linii w stopniach, znormalizowany do -90°..90° — etykieta biegnie
        // wzdłuż przęsła i nigdy nie stoi do góry nogami.
        let angle = (Math.atan2(a.uy, a.ux) * 180) / Math.PI;
        if (angle > 90) angle -= 180;
        if (angle < -90) angle += 180;
        // Prostopadłe odsunięcie od linii — zawsze na stronę "nad" obróconym
        // tekstem, żeby napis nie leżał na kresce.
        let px = -a.uy;
        let py = a.ux;
        if (py > 0) {
          px = -px;
          py = -py;
        }
        const lx = mx + px * 10;
        const ly = my + py * 10;
        return (
          <g key={a.numeral}>
            <line
              x1={cx}
              y1={cy}
              x2={x2}
              y2={y2}
              stroke="#000"
              strokeWidth="1.6"
            />
            <circle
              cx={x2}
              cy={y2}
              r="4"
              fill="#fff"
              stroke="#000"
              strokeWidth="1.4"
            />
            <text
              x={x2 + a.ux * 16}
              y={y2 + a.uy * 16 + 4}
              textAnchor="middle"
              fontSize="10"
              fontFamily="monospace"
            >
              {a.otherName}
            </text>
            <text
              x={lx}
              y={ly}
              textAnchor="middle"
              fontSize="10"
              fontFamily="monospace"
              transform={`rotate(${angle} ${lx} ${ly})`}
            >
              {a.numeral} — {fmtMeters(a.lengthM)}
              {a.manual ? " *" : ""}
            </text>
          </g>
        );
      })}

      <circle cx={cx} cy={cy} r="6.5" fill="#000" />
      <text
        x={cx}
        y={cy + 24}
        textAnchor="middle"
        fontSize="13"
        fontWeight="bold"
        fontFamily="monospace"
      >
        {poleDisplayLabel(pole.properties)}
      </text>
    </svg>
  );
}

function HeaderRow({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <tr>
      <td className="w-48 border border-black/60 px-2 py-1 text-[11px] font-bold uppercase">
        {label}
      </td>
      <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
        {value}
      </td>
    </tr>
  );
}

/**
 * Sam dokument karty obliczeń słupa (#tc-card) + jego reguły wydruku — bez
 * chrome'u aplikacji i bez routingu. Renderowany w dwóch miejscach:
 * TechCalcCardPage (podgląd + pobranie PDF) i eksport ZIP kart, gdzie każda
 * karta staje się samodzielnym plikiem HTML (renderToStaticMarkup). Dlatego
 * reguły @media print dotyczące shella aplikacji zostają na stronie — tu są
 * wyłącznie reguły samej karty, ważne także w pliku samodzielnym.
 */
export function TechCalcCardDocument({
  projectName,
  variantName,
  drawing,
  pole,
  result,
  stale,
}: {
  projectName: string;
  variantName: string;
  drawing: TechCalcDrawing | null | undefined;
  pole: TechCalcPointFeature;
  /** Wynik obliczeń tego słupa; undefined = nie policzono. */
  result: PoleResult | undefined;
  /** Rysunek zmieniony po obliczeniach — wyniki nieaktualne. */
  stale: boolean;
}) {
  const poleRef = pole.properties.ref;
  const points = drawingPoints(drawing);
  const nodesByRef = new Map(points.map((p) => [p.properties.ref, p]));
  const spans = drawingLines(drawing).filter(
    (l) => l.properties.from_ref === poleRef || l.properties.to_ref === poleRef,
  );
  const arms = buildArms(pole, spans, nodesByRef);
  const r = result;
  const params = pole.properties.params;

  return (
    <>
      <style>{`
        @media print {
          #tc-card {
            position: absolute; left: 0; top: 0; width: 100%;
            margin: 0; padding: 0; max-width: none;
            box-shadow: none; border: none;
            /* Twardy strażnik jednej strony: A4 297mm minus 2×12mm marginesu @page. */
            max-height: 272mm; overflow: hidden;
          }
          /* Obramowania tabel jako zwykłe bordery — drukują się także przy
             wyłączonym "Drukuj tła"; nagłówek = bold + border, bez tła. */
          #tc-card table, #tc-card th, #tc-card td { border: 1px solid #000 !important; }
          #tc-card thead th { font-weight: 700; background: none !important; border-bottom: 1.5px solid #000 !important; }
          /* Zbita typografia wydruku — ekranowa wersja zostaje przestronna. */
          #tc-card h1 { font-size: 13pt; margin-bottom: 1mm; }
          #tc-card .tc-subtitle { font-size: 8pt; margin-bottom: 2mm; }
          #tc-card h2 { font-size: 9pt; margin: 2mm 0 1mm; }
          #tc-card table { font-size: 9pt; margin-bottom: 2mm; }
          #tc-card th, #tc-card td { font-size: 8.5pt; padding: 0.5mm 1.5mm; }
          #tc-card pre { font-size: 8pt; line-height: 1.3; padding: 1.5mm; margin: 0; }
          /* Schemat: maks. ~1/3 użytecznej wysokości strony; przy >8 przęsłach mniejszy. */
          #tc-card .tc-schematic svg {
            display: block; margin: 0 auto; width: auto; max-width: 100%;
            max-height: var(--tc-schem-h, 88mm);
          }
          /* Przebiegi obliczeń X i Y obok siebie — to one przelewały na 2. stronę.
             Twardy limit wysokości przebiegu: obcina się ogon opisu, nigdy werdykt
             (WYNIK stoi nad przebiegami). */
          #tc-card .tc-desc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3mm; }
          #tc-card .tc-desc-grid pre { max-height: 58mm; overflow: hidden; }
          #tc-card .tc-sec { break-inside: avoid; }
          #tc-card .tc-status { font-size: 10pt; margin-top: 2mm; padding: 1mm 2mm; }
        }
        @page { size: A4; margin: 12mm; }
      `}</style>

      <div
        id="tc-card"
        className="mx-auto max-w-[840px] border border-black/30 bg-white p-8 text-black shadow-sm"
      >
        <h1 className="mb-1 text-center font-mono text-lg font-bold uppercase tracking-wider">
          Karta obliczeń słupa {poleDisplayLabel(pole.properties)}
        </h1>
        <p className="tc-subtitle mb-4 text-center font-mono text-[11px] text-black/70">
          Obliczenia techniczne sił wierzchołkowych słupów — GK ELEKTIM
        </p>

        <table className="mb-5 w-full border-collapse">
          <tbody>
            <HeaderRow label="Projekt" value={projectName} />
            <HeaderRow label="Wariant" value={variantName || "—"} />
            <HeaderRow label="Numer słupa" value={poleDisplayLabel(pole.properties)} />
            <HeaderRow label="Typ słupa" value={params.pole_type || "—"} />
            <HeaderRow label="Rodzaj słupa" value={params.construction || "—"} />
            <HeaderRow label="Materiał" value={params.material || "—"} />
            <HeaderRow
              label="Wysokość"
              value={params.height ? `${params.height} m` : "—"}
            />
            <HeaderRow label="Funkcja" value={params.function || "—"} />
            <HeaderRow
              label="Oprawa oświetleniowa"
              value={params.is_lighting ? "tak" : "nie"}
            />
          </tbody>
        </table>

        {stale && (
          <p className="mb-4 border border-black px-2 py-1 text-center font-mono text-[11px] font-bold uppercase">
            Uwaga: rysunek zmieniony po obliczeniach — wyniki nieaktualne
          </p>
        )}

        {/* ── schemat przęseł ────────────────────────────────────────────── */}
        <section className="tc-sec">
          <h2 className="mb-1 font-mono text-[12px] font-bold uppercase tracking-wider">
            1. Schemat przęseł (kierunki wg współrzędnych, północ u góry)
          </h2>
          {arms.length ? (
            <div
              className="tc-schematic"
              // Dużo przęseł → dłuższa tabela zestawienia, więc schemat na
              // wydruku dodatkowo maleje, by karta została jednostronicowa.
              style={
                { "--tc-schem-h": arms.length > 8 ? "60mm" : "88mm" } as React.CSSProperties
              }
            >
              <StarSchematic pole={pole} arms={arms} />
            </div>
          ) : (
            <p className="py-4 text-center font-mono text-[11px] text-black/60">
              Do słupa nie jest podłączone żadne przęsło.
            </p>
          )}
        </section>

        {/* ── zestawienie przęseł ────────────────────────────────────────── */}
        <h2 className="mt-4 mb-1 font-mono text-[12px] font-bold uppercase tracking-wider">
          2. Zestawienie przęseł
        </h2>
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {["Nr", "Od – do", "Długość", "Przewody"].map((h) => (
                <th
                  key={h}
                  className="border border-black/60 bg-black/5 px-2 py-1 text-left text-[11px] font-bold uppercase"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {arms.map((a) => (
              <tr key={a.numeral}>
                <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                  {a.numeral}
                </td>
                <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                  {nodeDisplayName(
                    nodesByRef.get(a.span.properties.from_ref),
                    a.span.properties.from_ref,
                    true,
                  )}{" "}
                  –{" "}
                  {nodeDisplayName(
                    nodesByRef.get(a.span.properties.to_ref),
                    a.span.properties.to_ref,
                    true,
                  )}
                </td>
                <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                  {fmtMeters(a.lengthM)}
                  {a.manual ? " *" : ""}
                </td>
                <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                  {fmtBundles(a.span.properties.bundles)}
                </td>
              </tr>
            ))}
            {!arms.length && (
              <tr>
                <td
                  colSpan={4}
                  className="border border-black/60 px-2 py-2 text-center font-mono text-[11px]"
                >
                  — brak przęseł —
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {arms.some((a) => a.manual) && (
          <p className="mt-0.5 font-mono text-[9px] text-black/60">
            * długość nadpisana ręcznie (zamiast długości z geometrii)
          </p>
        )}

        {/* ── wyniki ─────────────────────────────────────────────────────── */}
        <h2 className="mt-5 mb-1 font-mono text-[12px] font-bold uppercase tracking-wider">
          3. Wyniki obliczeń
        </h2>
        {!r ? (
          <p className="py-3 font-mono text-[11px] text-black/60">
            Brak wyników obliczeń dla tego słupa — przelicz wariant w zakładce
            „Wyniki".
          </p>
        ) : !r.ok ? (
          <p className="py-3 font-mono text-[12px] font-bold">
            Błąd obliczeń: {r.error || r.error_type || "nieznany błąd"}
          </p>
        ) : (
          <>
            <table className="mb-3 w-full border-collapse">
              <thead>
                <tr>
                  {[
                    "Kierunek",
                    "Siła obliczeniowa",
                    "Siła dopuszczalna",
                    "Warunek",
                  ].map((h) => (
                    <th
                      key={h}
                      className="border border-black/60 bg-black/5 px-2 py-1 text-left text-[11px] font-bold uppercase"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    X
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    {fmtForce(r.target_force_x)}
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    {fmtForce(r.max_allowed_target_force_x)}
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px] font-bold">
                    {r.passed_x ? "SPEŁNIONY" : "NIESPEŁNIONY"}
                  </td>
                </tr>
                <tr>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    Y
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    {fmtForce(r.target_force_y)}
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px]">
                    {fmtForce(r.max_allowed_target_force_y)}
                  </td>
                  <td className="border border-black/60 px-2 py-1 font-mono text-[12px] font-bold">
                    {r.passed_y ? "SPEŁNIONY" : "NIESPEŁNIONY"}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Werdykt PRZED przebiegami — przy bardzo długich opisach strażnik
                jednej strony obcina ogon przebiegu, nigdy wynik. */}
            <p className="tc-status tc-sec mb-3 border-2 border-black px-3 py-2 text-center font-mono text-sm font-bold uppercase">
              Warunek: {r.passed ? "SPEŁNIONY" : "NIESPEŁNIONY"}
            </p>

            {/* Przebiegi X i Y obok siebie — jedna pod drugą przelewały kartę
                na drugą stronę A4. */}
            {/* Przebieg = wzór + wartości podstawione pod jego składniki +
                wykonane działanie (backend: result_*_steps). */}
            <div className="tc-desc-grid grid gap-3 md:grid-cols-2">
              {([
                ["X", calcSteps(r.result_x_steps, r.result_x_description)],
                ["Y", calcSteps(r.result_y_steps, r.result_y_description)],
              ] as const).map(([axis, lines]) =>
                lines.length ? (
                  <div key={axis} className="tc-sec">
                    <p className="mb-0.5 font-mono text-[11px] font-bold uppercase">
                      Kierunek {axis} — przebieg obliczeń
                    </p>
                    <pre className="overflow-x-auto whitespace-pre-wrap border border-black/30 bg-black/5 p-2 font-mono text-[10px] leading-relaxed">
                      {lines.join("\n")}
                    </pre>
                  </div>
                ) : null,
              )}
            </div>

          </>
        )}
      </div>
    </>
  );
}
